import React, { useMemo, useCallback } from "react"
import { Box, Typography, Button } from "@material-ui/core"
import { OpenInNew as OpenInNewIcon } from "@material-ui/icons"

import useStyles from "@/pages/Attendance/ReferralPopup/screens/ReferralDoneScreen/styles"
import {
	REFERRAL_PARTNER_WALLET_URL,
	REFERRAL_VALIDITY_DAYS,
	channelToLabel
} from "@/pages/Attendance/ReferralPopup/constants"
import {
	ReferralStatus,
	ReferralChannel,
	ReferralDoneScreenProps
} from "@/pages/Attendance/ReferralPopup/types"

type StepState = "completed" | "current" | "pending"

type StepperStep = {
	label: string
	state: StepState
}

const statusToStepStates: Record<ReferralStatus, StepState[]> = {
	registered: ["current", "pending", "pending"],
	negotiating: ["completed", "current", "pending"],
	released: ["completed", "completed", "completed"]
}

const gestorStepLabels = ["registrada", "em negociação", "verba liberada"]
const atendenteStepLabels = ["registrada", "em negociação", "resgate liberado"]

const buildPayoutNote = (isAdmin: boolean, channel: ReferralChannel, payoutMethod: string): string => {
	if (isAdmin) {
		const channelName = channelToLabel[channel]
		return `Assim que ela fechar, sua verba no ${channelName} é liberada.`
	}

	return payoutMethod === "cash"
		? "Assim que ela fechar, o Pix cai na sua conta."
		: "Assim que ela fechar, o valor entra como desconto na sua próxima fatura."
}

const buildWalletCountLabel = (count: number): string => {
	return count === 1 ? "1 indicação" : `${count} indicações`
}

const ReferralDoneScreen: React.FC<ReferralDoneScreenProps> = (props) => {
	const {
		isAdmin,
		leadName,
		walletData,
		formData,
		onIndicateAnother
	} = props
	const classes = useStyles()

	const latestReferralStatus = useMemo<ReferralStatus>(() => {
		if (walletData.referrals.length === 0) {
			return "registered"
		}

		const sortedReferrals = [...walletData.referrals].sort((first, second) => {
			return new Date(second.createdAt).getTime() - new Date(first.createdAt).getTime()
		})

		return sortedReferrals[0].status
	}, [walletData.referrals])

	const stepStates = statusToStepStates[latestReferralStatus]
	const stepLabels = isAdmin ? gestorStepLabels : atendenteStepLabels

	const stepperSteps = useMemo<StepperStep[]>(() => {
		return stepLabels.map((label, index) => ({
			label,
			state: stepStates[index]
		}))
	}, [stepLabels, stepStates])

	const payoutNote = buildPayoutNote(
		isAdmin,
		formData.channel,
		formData.payoutMethod
	)

	const walletCountLabel = buildWalletCountLabel(walletData.walletCount)
	const validityDays = walletData.validityDays || REFERRAL_VALIDITY_DAYS

	const handleIndicateAnotherClick = useCallback(() => {
		onIndicateAnother()
	}, [onIndicateAnother])

	const stepStateToClassName: Record<StepState, string> = {
		completed: `${classes.stepCircle} ${classes.stepCircleCompleted}`,
		current: `${classes.stepCircle} ${classes.stepCircleCurrent}`,
		pending: `${classes.stepCircle} ${classes.stepCirclePending}`
	}

	const renderStepCircle = (state: StepState): React.ReactNode => {
		const shouldShowGlyph = state === "completed" || state === "current"

		return (
			<Box className={stepStateToClassName[state]}>
				{shouldShowGlyph && (
					<Typography component="span" className={classes.stepCircleGlyph}>{"✓"}</Typography>
				)}
			</Box>
		)
	}

	const renderStepConnector = (state: StepState, isLastStep: boolean): React.ReactNode => {
		if (isLastStep) {
			return null
		}

		const connectorClassName = state === "completed"
			? `${classes.stepConnector} ${classes.stepConnectorCompleted}`
			: `${classes.stepConnector} ${classes.stepConnectorPending}`

		return <Box className={connectorClassName} />
	}

	const renderStepper = (): React.ReactNode => {
		return (
			<Box className={classes.stepper}>
				{stepperSteps.map((step, index) => {
					const isLastStep = index === stepperSteps.length - 1
					const labelClassName = step.state === "completed" || step.state === "current"
						? `${classes.stepLabel} ${classes.stepLabelCompleted}`
						: classes.stepLabel

					return (
						<React.Fragment key={step.label}>
							<Box className={classes.stepWrapper}>
								{renderStepCircle(step.state)}
								<Typography className={labelClassName}>
									{step.label}
								</Typography>
							</Box>
							{renderStepConnector(step.state, isLastStep)}
						</React.Fragment>
					)
				})}
			</Box>
		)
	}

	return (
		<Box>
			<Box className={classes.header}>
				<Box className={classes.titleRow}>
					<Box className={classes.checkCircle}>
						<Typography component="span" className={classes.checkGlyph}>
							{"✓"}
						</Typography>
					</Box>
					<Typography className={classes.title}>
						{"Indicação registrada."}
					</Typography>
				</Box>
				<Typography className={classes.subtitle}>
					{"Avisamos você assim que "}
					<Box component="span" className={classes.leadNameBold}>
						{leadName}
					</Box>
					{` entrar em negociação. ${payoutNote}`}
				</Typography>
			</Box>
			<Box className={classes.walletCard}>
				<Box className={classes.walletTopSection}>
					<Box className={classes.walletLeftColumn}>
						<Typography className={classes.walletLabel}>
							{isAdmin ? "SUA VERBA" : "SUA CARTEIRA"}
						</Typography>
						<Typography className={classes.walletTotal}>
							{`R$ ${walletData.walletTotal}`}
						</Typography>
						<Typography className={classes.walletStatus}>
							{"aguardando"}
						</Typography>
					</Box>
					<Box className={classes.walletRightColumn}>
						<Typography className={classes.walletCount}>
							{walletCountLabel}
						</Typography>
						<Typography className={classes.walletValidity}>
							{`válido por ${validityDays} dias`}
						</Typography>
					</Box>
				</Box>
				<Box className={classes.walletBottomSection}>
					{renderStepper()}
				</Box>
			</Box>
			<Box className={classes.footer}>
				<Button
					type="button"
					className={classes.indicateAnotherLink}
					onClick={handleIndicateAnotherClick}
					disableRipple
				>
					{"+ Indicar outra empresa"}
				</Button>
				<Button
					component="a"
					href={REFERRAL_PARTNER_WALLET_URL}
					target="_blank"
					rel="noopener noreferrer"
					type="button"
					className={classes.partnerButton}
					classes={{ label: classes.partnerButtonLabel }}
					disableElevation
				>
					{"Acompanhar no painel do parceiro"}
					<OpenInNewIcon className={classes.externalArrowIcon} />
				</Button>
			</Box>
		</Box>
	)
}

export default ReferralDoneScreen
